import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../models/Storefront';
import './StorefrontEditor.css';
import StorefrontHeaderEditor from './Header/StorefrontHeaderEditor';
import StorefrontAutocompleteStylePanel from './StorefrontAutocompleteStylePanel';
import StorefrontFooterEditor from './Footer/StorefrontFooterEditor';
import RawJsonEditor from './RawJsonEditor';
import { CommandBar, DefaultButton, ICommandBarItemProps, Panel, PanelType, PrimaryButton, Stack, Toggle } from '@fluentui/react';
import NumberField from '../../Inputs/NumberField';
import PanelWrapper from '../../Common/PanelWrapper';
import { parseHeaderLinksConfig } from './headerLinksConfig';
import { parseNotificationsConfig } from './notificationsConfig';
import StorefrontNotificationsEditor from './Notifications/StorefrontNotificationsEditor';
import StorefrontAccountPersonaStylePanel from './Header/StorefrontAccountPersonaStylePanel';
import { parseHeaderConfig, parseFooterConfig, lintChromeConfig } from './headerFooterConfig';
import { resolvedStyleValue } from './styleConfig';
import StorefrontLayoutSection from './Layout/StorefrontLayoutSection';
import { ILayout } from './layout';
import PreviewCanvas from './PreviewCanvas';
import { useUrlPanelState } from '../../../hooks/useUrlState';
import Setting from '../../Common/Setting';
import ConditionalSetting from '../../Common/ConditionalSetting';

// Header and footer are shared across every storefront page, so they get their own
// dedicated tab. Editing them here is the only place they can be changed — the Home
// and Search tabs render them read-only for page context.
interface IStorefrontHeaderFooterEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    storefrontHeaderLinks: IStorefrontLink[];
    globalHeaderLinks: IStorefrontLink[];
    onHeaderLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontFooterLinks: IStorefrontLink[];
    globalFooterLinks: IStorefrontLink[];
    onFooterLinksChange: (newLinks: IStorefrontLink[]) => void;
}

const StorefrontHeaderFooterEditor: React.FC<IStorefrontHeaderFooterEditorProps> = (props) => {
    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fff';

    // Which layout panel (if any) is open. The advanced header/footer grid editors live in their own
    // slide-out panels (launched from the command bar, or from the hover-pencil on each preview)
    // rather than inline, to keep this screen scannable.
    const [layoutPanel, setLayoutPanel] = React.useState<'header' | 'footer' | null>(null);
    // The header behavior settings (sticky, search row, bar height) moved out of an always-open box
    // into their own flyout, so the page leads with the preview like every other page editor does.
    // URL-driven (?panel=headerSettings) so a link to one of its fields can open it, the same
    // treatment every other toolbar panel here already has.
    const settingsPanelState = useUrlPanelState('headerSettings');
    const settingsPanel = settingsPanelState.isOpen;
    // Only one surface at a time: opening either layout panel dismisses the settings flyout.
    const openLayout = (region: 'header' | 'footer') => { settingsPanelState.close(); setLayoutPanel(region); };

    // The notification bars above the header — chrome shared by every page, so they belong to this
    // tab, and like the layout editors they get a flyout rather than an inline box.
    // URL-driven so a link to a notification bar field can open the panel it lives in.
    const notificationsPanelState = useUrlPanelState('notifications');
    const notificationsPanel = notificationsPanelState.isOpen;
    // The header's account/sign-in persona styling.
    const [accountPanel, setAccountPanel] = React.useState(false);

    // Header, footer, header-links and notifications each live in their own settings field, but the
    // merchant edits them together as one advanced object `{ header, footer, headerLinks,
    // notifications }` — so the page has exactly one Raw JSON control (kept out of the visual
    // preview). Edits are split back to the four fields on change.
    const headerConfig = React.useMemo(() => parseHeaderConfig(props.storefrontSettings.headerConfig), [props.storefrontSettings.headerConfig]);
    const footerConfig = React.useMemo(() => parseFooterConfig(props.storefrontSettings.footerConfig), [props.storefrontSettings.footerConfig]);
    const headerLinks = React.useMemo(() => parseHeaderLinksConfig(props.storefrontSettings.headerLinksConfig), [props.storefrontSettings.headerLinksConfig]);
    const notifications = React.useMemo(() => parseNotificationsConfig(props.storefrontSettings.notificationsConfig), [props.storefrontSettings.notificationsConfig]);
    // Store-name-beside-the-logo state, derived from headerConfig.titleText: only the empty string
    // hides the name, so absent (the default) and any custom override both read as "shown". A custom
    // override is called out under the toggle since flipping it off and on again drops that text.
    const showStoreName = headerConfig.titleText !== '';
    const hasCustomTitleText = typeof headerConfig.titleText === 'string' && headerConfig.titleText !== '';
    const combinedChrome = React.useMemo(
        () => ({ header: headerConfig, footer: footerConfig, headerLinks, notifications }),
        [headerConfig, footerConfig, headerLinks, notifications]);
    // RawJsonEditor applies `lintResult.parsed` on every valid keystroke, but lintChromeConfig
    // reports validity WITHOUT handing back the parsed object, so plugged in raw a valid edit would
    // apply the string "undefined". Re-attach it here rather than changing the shared linter, which
    // the storefront runtime mirrors. The second parse is cheap and cannot throw: lintChromeConfig
    // only returns valid when the same text already parsed.
    const lintChrome = (raw: string) => {
        const r = lintChromeConfig(raw);
        return { ...r, parsed: r.valid ? JSON.parse(raw) : undefined };
    };
    const applyCombinedChrome = (json: string) => {
        let parsed: { header?: unknown; footer?: unknown; headerLinks?: unknown; notifications?: unknown };
        try { parsed = JSON.parse(json); } catch { return; }
        props.onStorefrontSettingsChange({
            ...props.storefrontSettings,
            headerConfig: JSON.stringify(parsed.header ?? {}),
            footerConfig: JSON.stringify(parsed.footer ?? {}),
            headerLinksConfig: JSON.stringify(parsed.headerLinks ?? {}),
            notificationsConfig: JSON.stringify(parsed.notifications ?? {}),
        });
    };

    // This page has no style-section bar: header and footer styling is edited in place on the
    // preview below. What the bar carries instead is the page's three non-visual surfaces, each
    // with a `title` saying what it opens (matching the other page editors' bars).
    // The header search bar's suggestion-panel styling. Opened from the command bar above, or from the
    // search bar element itself; a single owner here so the two entry points cannot disagree.
    const [suggestionsPanel, setSuggestionsPanel] = React.useState(false);

    const chromeBarItems: ICommandBarItemProps[] = [
        { key: 'settings', text: 'Header Settings', title: 'The store name beside the logo, sticky header, the search row, and the header bar height.', iconProps: { iconName: 'Settings' }, checked: settingsPanel, onClick: () => { setLayoutPanel(null); settingsPanelState.open(); } },
        { key: 'headerLayout', text: 'Header Layout', title: 'Arrange the header into a custom grid of rows and cells.', iconProps: { iconName: 'GridViewMedium' }, checked: layoutPanel === 'header', onClick: () => openLayout('header') },
        { key: 'footerLayout', text: 'Footer Layout', title: 'Arrange the footer into a custom grid of rows and cells.', iconProps: { iconName: 'GridViewMedium' }, checked: layoutPanel === 'footer', onClick: () => openLayout('footer') },
        { key: 'headerSuggestions', text: 'Search Suggestions', title: 'Style the suggestion panel that drops below the header search bar.', iconProps: { iconName: 'Color' }, checked: suggestionsPanel, onClick: () => setSuggestionsPanel(true) },
        { key: 'notifications', text: 'Notification Bars', title: 'Scheduled, dismissable bars above the header.', iconProps: { iconName: 'Ringer' }, checked: notificationsPanel, onClick: () => notificationsPanelState.open() },
        { key: 'account', text: 'Account & Sign In', title: 'Style the account persona shoppers click to sign in.', iconProps: { iconName: 'Contact' }, checked: accountPanel, onClick: () => setAccountPanel(true) },
    ];

    return (
        <div>
            {props.editMode && (
                <div style={{ marginBottom: '1em' }}>
                <RawJsonEditor
                    label='Header & Footer'
                    liveObject={combinedChrome}
                    onApply={applyCombinedChrome}
                    lint={lintChrome}
                    keysHint='Four sections: header, footer, headerLinks, notifications. header: sticky (boolean) plus bar, title, searchBar, menuButton, menu. footer: bar, leftText, rightText. headerLinks: container, cellStyle (style objects) and links [{ label, url, iconUrl, newTab, style }]. notifications: bars [{ id, name, text, linkText, linkUrl, startsAt, endsAt, dismissMode, revision, bar, message, link, closeButton }]. Any camelCase CSS property works; numbers are px, fontSize is a percent.'
                >
                    <CommandBar
                        items={chromeBarItems}
                        styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                    />
                </RawJsonEditor>
                {/* Header behavior settings: a right-side flyout like the other editors' style
                    panels. Non-blocking so the preview keeps repainting as the toggles flip. */}
                <Panel isOpen={settingsPanel} onDismiss={() => settingsPanelState.close()} headerText='Header Settings' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
                        <Setting id='storefront.header.sticky'>
                        <div>
                            {/* The live storefront shows headerConfig.titleText when it is a string and the
                                storefront name when the key is absent, so '' is how a store renders the logo
                                alone. On therefore DELETES the key rather than writing the name into it:
                                writing the name would freeze it and a later rename would not follow. */}
                            <Toggle
                                inlineLabel
                                label='Show store name next to logo'
                                checked={showStoreName}
                                onChange={(_, checked) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify({ ...headerConfig, titleText: checked ? undefined : '' }) })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                                Off shows the logo by itself. On uses the storefront's name, so renaming the store updates the header too.
                            </div>
                            {hasCustomTitleText && (
                                <div style={{ fontSize: 11, color: '#a4262c', marginTop: 4 }}>
                                    A custom title override is set ("{headerConfig.titleText}"). Turning this off and back on discards it and returns to the storefront name.
                                </div>
                            )}
                        </div>
                        <div>
                            <Toggle
                                inlineLabel
                                label='Sticky header (stays pinned while scrolling)'
                                checked={headerConfig.sticky ?? false}
                                onChange={(_, checked) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify({ ...headerConfig, sticky: !!checked }) })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                                When on, the header bar stays fixed at the top of the page and only the content below it scrolls.
                            </div>
                        </div>
                        </Setting>
                        <div>
                            <Setting id='storefront.header.searchRow'>
                            <Toggle
                                inlineLabel
                                label='Search bar as a row below the header'
                                checked={headerConfig.searchRow ?? false}
                                onChange={(_, checked) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify({ ...headerConfig, searchRow: checked || undefined }) })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                            </Setting>
                            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                                Moves the header search out of the top row into its own full-width row beneath the header (needs the header search enabled).
                            </div>
                            <ConditionalSetting when={!!headerConfig.searchRow} id='storefront.header.searchRowSticky'>
                                <Setting id='storefront.header.searchRowSticky'>
                                <Toggle
                                    inlineLabel
                                    label='Sticky search row (pins on scroll, independent of the header)'
                                    checked={headerConfig.searchRowSticky ?? false}
                                    onChange={(_, checked) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify({ ...headerConfig, searchRowSticky: checked || undefined }) })}
                                    styles={{ root: { marginTop: 8, marginBottom: 0 } }}
                                />
                                </Setting>
                            </ConditionalSetting>
                        </div>
                        <div>
                            <NumberField
                                inline
                                width={260}
                                label='Header bar height'
                                suffix='px'
                                placeholder='Auto'
                                settingId='storefront.header.barHeight'
                                value={typeof headerConfig.bar?.height === 'number' ? headerConfig.bar.height : undefined}
                                onChange={(v) => {
                                    // Writes bar.height + bar.minHeight so the value also beats the live
                                    // store's 48px min-height floor; empty restores automatic sizing.
                                    // Values below 24px are clamped — the bar can't render sanely below that.
                                    const bar = { ...headerConfig.bar };
                                    if (v === undefined) { delete bar.height; delete bar.minHeight; }
                                    else { const h = Math.max(24, v); bar.height = h; bar.minHeight = h; }
                                    const next = { ...headerConfig, bar: Object.keys(bar).length ? bar : undefined };
                                    props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify(next) });
                                }}
                            />
                            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
                                Empty = automatic: the bar grows to fit the logo, links and search (~76px with header search on). Smaller values keep the content vertically centered; below ~40px the search box and a default-size (3em) logo will start to poke out — shrink the logo via the advanced JSON (header.logo.height) to go really compact. Minimum 24.
                            </div>
                        </div>
                    </Stack>
                    {/* Way out to the grid layout editor, which is the next thing a merchant reaches
                        for after the bar height. It swaps panels rather than stacking one over the
                        other: the layout panel is PanelType.large and would bury this one. */}
                    <div style={{ marginTop: '1em', borderTop: '1px solid #edebe9', paddingTop: '1em' }}>
                        <DefaultButton
                            text='Header layout…'
                            iconProps={{ iconName: 'GridViewMedium' }}
                            title='Arrange the header into a custom grid of rows and cells.'
                            onClick={() => openLayout('header')}
                            styles={{ root: { width: '100%' } }}
                        />
                    </div>
                </Panel>
                </div>
            )}
            <PreviewCanvas style={{ backgroundColor }}>
            <StorefrontAutocompleteStylePanel
                context='header'
                isOpen={suggestionsPanel}
                onDismiss={() => setSuggestionsPanel(false)}
                storefrontSettings={props.storefrontSettings}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
            />
            <StorefrontNotificationsEditor
                isOpen={notificationsPanel}
                onDismiss={() => notificationsPanelState.close()}
                storefrontSettings={props.storefrontSettings}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
            />
            <StorefrontAccountPersonaStylePanel
                isOpen={accountPanel}
                onDismiss={() => setAccountPanel(false)}
                storefrontSettings={props.storefrontSettings}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
            />
            <StorefrontHeaderEditor
                onEditSuggestionStyles={() => setSuggestionsPanel(true)}
                storefrontSettings={props.storefrontSettings}
                storefrontTheme={props.storefrontTheme}
                globalSettings={props.globalSettings}
                globalTheme={props.globalTheme}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                editMode={props.editMode}
                storefrontLinks={props.storefrontHeaderLinks}
                globalLinks={props.globalHeaderLinks}
                onLinksChange={props.onHeaderLinksChange}
                onEditLayout={props.editMode ? () => openLayout('header') : undefined}
            />
            {/* Empty space standing in for the page body between the header and footer. */}
            <div
                style={{
                    minHeight: 240,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <span style={{ color: '#9a9a9a', fontStyle: 'italic', fontSize: 13 }}>
                    Page content appears between the header and footer
                </span>
            </div>
            <StorefrontFooterEditor
                storefrontSettings={props.storefrontSettings}
                storefrontTheme={props.storefrontTheme}
                globalSettings={props.globalSettings}
                globalTheme={props.globalTheme}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                editMode={props.editMode}
                footerLinks={props.storefrontFooterLinks}
                globalFooterLinks={props.globalFooterLinks}
                onEditLayout={props.editMode ? () => openLayout('footer') : undefined}
            />
            </PreviewCanvas>
            <PanelWrapper
                isOpen={layoutPanel !== null}
                onDismiss={() => setLayoutPanel(null)}
                type={PanelType.large}
                isFooterAtBottom
                title={layoutPanel === 'footer' ? 'Footer layout' : 'Header layout'}
                footerButtons={[<PrimaryButton key='done' text='Done' onClick={() => setLayoutPanel(null)} />]}
            >
                {layoutPanel && (
                    <StorefrontLayoutSection
                        region={layoutPanel}
                        settings={props.storefrontSettings}
                        layout={layoutPanel === 'header' ? headerConfig.layout : footerConfig.layout}
                        onChange={(layout: ILayout | undefined) =>
                            layoutPanel === 'header'
                                ? props.onStorefrontSettingsChange({ ...props.storefrontSettings, headerConfig: JSON.stringify({ ...headerConfig, layout }) })
                                : props.onStorefrontSettingsChange({ ...props.storefrontSettings, footerConfig: JSON.stringify({ ...footerConfig, layout }) })}
                    />
                )}
            </PanelWrapper>
        </div>
    );
};

export default StorefrontHeaderFooterEditor;
